import { useEffect } from 'react';

import { SILENT_ERRORS_CONTEXT, urqlClient } from '@shared/api';
import {
  CURRENT_USER_SECTION_ACCESS,
  type CurrentUserPermissionsData,
} from '@shared/hooks/use-section-access';
import { SECTION_CODES } from '@shared/lib/rbac-sections';

import { useAiDataPolicyStore, type AiDataPolicyRole } from './store';

export type { AiDataPolicyRole } from './store';

export function aiDataPolicyRoleOf(
  payload: CurrentUserPermissionsData['currentUserPermissions'],
): AiDataPolicyRole {
  if (payload?.isSuperAdmin) return 'admin';
  const entry = payload?.permissions?.find((p) => p?.section === SECTION_CODES.aiDataPolicy);
  return entry?.level === 'FULL_ACCESS' ? 'admin' : 'member';
}

let roleInflight: Promise<AiDataPolicyRole> | null = null;

function readPermissions() {
  try {
    return urqlClient
      .query<CurrentUserPermissionsData>(
        CURRENT_USER_SECTION_ACCESS,
        {},
        { requestPolicy: 'cache-first', ...SILENT_ERRORS_CONTEXT },
      )
      .toPromise();
  } catch (err) {
    return Promise.reject(err instanceof Error ? err : new Error(String(err)));
  }
}

export function fetchAiDataPolicyRole(): Promise<AiDataPolicyRole> {
  roleInflight ??= readPermissions()
    .then((result): AiDataPolicyRole => {
      if (result.error || !result.data) return 'member';
      return aiDataPolicyRoleOf(result.data.currentUserPermissions);
    })
    .catch((): AiDataPolicyRole => 'member')
    .then((role) => {
      useAiDataPolicyStore.setState({ role });
      return role;
    })
    .finally(() => {
      roleInflight = null;
    });
  return roleInflight;
}

export function useAiDataPolicyRole(): AiDataPolicyRole | null {
  const role = useAiDataPolicyStore((s) => s.role);
  useEffect(() => {
    if (role === null) void fetchAiDataPolicyRole();
  }, [role]);
  return role;
}
